import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Typography, Button, TextField, Chip, CircularProgress,
    Dialog, DialogContent, InputAdornment, MenuItem, TablePagination,
} from '@mui/material';
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined';
import CloseIcon from '@material-ui/icons/Close';
import {
    listProfileChangeRequestsService,
    reviewProfileChangeRequestService,
} from '../../../services/client.service';
import { getCountryLabel, getStatesByCountry, useGeo } from '../../../utils/geo';
import { toast } from '../../../utils/utils';
import { TOAST_TYPES, PROFILE_CHANGE_STATUS } from '../../../utils/constants';

/**
 * School Workspace > Profile Change Requests.
 *
 * Learners are shown the details their school entered the first time they sign
 * in to DQ World, and correct them if anything is wrong. Those corrections land
 * here. **Nothing on a learner's profile changes until it is approved on this
 * screen** — that is the whole point of the queue, and the page says so under
 * the title so a reviewer never assumes a pending row has already taken effect.
 *
 * DQ World never shows this queue. A learner sees only that their request was
 * sent; the decision belongs to the portal their school already uses.
 */

const NAVY = 'var(--brand-text, #052F44)';
const ORANGE = '#FF6900';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const BORDER = 'var(--brand-border, #E5E7EB)';

const outlineBtnSx = {
    textTransform: 'none', fontWeight: 700, fontSize: '13px',
    color: ORANGE, borderColor: ORANGE, borderRadius: '50px', px: 2.5, py: 0.6,
    '&:hover': { borderColor: '#e55e00', backgroundColor: 'rgba(255,105,0,0.04)' },
};
const primaryBtnSx = {
    textTransform: 'none', fontWeight: 700, fontSize: '14px',
    backgroundColor: ORANGE, color: '#fff', borderRadius: '50px', px: 3.5, py: 1,
    '&:hover': { backgroundColor: '#e55e00' },
};


/**
 * A stored value as a person reads it.
 *
 * The profile keeps an ISO country code, and a state that may be either a
 * name (typed on the demographic form) or an ISO 3166-2 code (imported from
 * a class CSV). A reviewer deciding whether the old value was wrong cannot
 * do that against "AE-AZ", so both shapes are resolved to the name here and
 * anything unrecognised is shown exactly as stored rather than blanked.
 */
const prettyValue = (field, raw) => {
    const v = String(raw ?? '').trim();
    if (!v) return '';

    if (field === 'country') return getCountryLabel(v) || v;

    if (field === 'state') {
        /*
         * An ISO 3166-2 code carries its country in the prefix — AE-AZ is Abu
         * Dhabi — so the name resolves without knowing which country the
         * learner was in when the value was written, which the request does
         * not record. A state stored as a name has no prefix, finds nothing,
         * and falls through unchanged.
         */
        const match = getStatesByCountry(v.split('-')[0]).find((st) => st.code === v);
        return match?.name || v;
    }

    // Stored lower-case by the demographic form, and by approval.
    if (field === 'gender') return v.charAt(0).toUpperCase() + v.slice(1);

    return v;
};

/** Status chip — the three the queue can show, in the mock-up's colours. */
const STATUS_CHIP = {
    [PROFILE_CHANGE_STATUS.PENDING]:  { label: 'Pending',  color: '#B14E00', bg: 'rgba(255,105,0,0.12)' },
    [PROFILE_CHANGE_STATUS.APPROVED]: { label: 'Approved', color: '#166534', bg: 'rgba(22,101,52,0.12)' },
    [PROFILE_CHANGE_STATUS.REJECTED]: { label: 'Rejected', color: '#4B5563', bg: 'rgba(107,114,128,0.14)' },
};

const StatusChip = ({ status }) => {
    const tone = STATUS_CHIP[Number(status)] || STATUS_CHIP[PROFILE_CHANGE_STATUS.PENDING];
    return (
        <Chip
            label={tone.label}
            size="small"
            sx={{
                backgroundColor: tone.bg, color: tone.color,
                fontWeight: 700, fontSize: '12px', borderRadius: '50px',
            }}
        />
    );
};

/** The labels the learner saw, so the review reads the same words they did. */
const FIELD_LABELS = {
    first_name: 'First name',
    last_name: 'Last name',
    student_email: 'My email',
    year_of_birth: 'Year of birth',
    gender: 'Gender',
    country: 'Country',
    state: 'State / Province / Region',
    postal_code: 'Postal / ZIP code',
};

const fmtDate = (d) => {
    if (!d) return '—';
    const when = new Date(d);
    return Number.isNaN(when.getTime()) ? '—' : when.toISOString().slice(0, 10);
};

const COLS = '1.6fr 0.8fr 1fr 1fr 0.9fr 0.9fr';

/**
 * The review dialog: old value struck through beside the new one, for every
 * field the learner changed and nothing else.
 *
 * Read-only once decided — a request that has been approved or rejected shows
 * the same comparison with the buttons gone, which is what the queue's "View"
 * opens.
 */
const ReviewDialog = ({ open, request, onClose, onDecide, busy }) => {
    if (!request) return null;
    const pending = Number(request.status) === PROFILE_CHANGE_STATUS.PENDING;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '22px', fontWeight: 800, color: NAVY }}>
                        {request.learner_name || 'Learner'}
                    </Typography>
                    <Button onClick={onClose} sx={{ minWidth: 0, p: 0.5, color: MUTED }}>
                        <CloseIcon style={{ fontSize: 20 }} />
                    </Button>
                </Box>
                <Typography sx={{ fontSize: '13px', color: MUTED, mb: 3 }}>
                    {[request.class_name, `submitted ${fmtDate(request.createdAt)}`]
                        .filter(Boolean).join(' · ')}
                </Typography>

                <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', overflow: 'hidden' }}>
                    <Box sx={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2,
                        px: 2.5, py: 1.5, backgroundColor: 'var(--brand-bg, #F9FAFB)',
                        borderBottom: `1px solid ${BORDER}`,
                    }}>
                        {['Field', 'Was', 'Now'].map((h) => (
                            <Typography key={h} sx={{
                                fontSize: '11.5px', fontWeight: 700, color: MUTED,
                                textTransform: 'uppercase', letterSpacing: '0.5px',
                            }}>{h}</Typography>
                        ))}
                    </Box>
                    {(request.changes || []).map((c) => (
                        <Box key={c.field} sx={{
                            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2,
                            px: 2.5, py: 2, borderBottom: `1px solid ${BORDER}`,
                            '&:last-of-type': { borderBottom: 'none' },
                        }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: NAVY }}>
                                {FIELD_LABELS[c.field] || c.field}
                            </Typography>
                            {/* Struck through, not hidden: the reviewer is deciding
                                whether the OLD value was wrong, so it has to stay
                                legible next to the new one. */}
                            <Typography sx={{ fontSize: '14px', color: MUTED, textDecoration: 'line-through' }}>
                                {prettyValue(c.field, c.was) || '—'}
                            </Typography>
                            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: NAVY }}>
                                {prettyValue(c.field, c.now) || '—'}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {pending ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                        <Button variant="outlined" sx={outlineBtnSx} disabled={busy}
                                onClick={() => onDecide(false)}>
                            Reject
                        </Button>
                        <Button sx={primaryBtnSx} disabled={busy}
                                onClick={() => onDecide(true)}>
                            {busy ? 'Saving…' : 'Approve'}
                        </Button>
                    </Box>
                ) : (
                    <Typography sx={{ fontSize: '13px', color: MUTED, mt: 3, textAlign: 'right' }}>
                        {Number(request.status) === PROFILE_CHANGE_STATUS.APPROVED
                            ? 'Approved — these values are on the learner’s profile.'
                            : 'Rejected — the learner’s profile was left unchanged.'}
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
};

const ProfileChangeRequestsPage = () => {
    useGeo(); // re-render with the API's names once they load
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    /*
     * Server-side: the page and size go to the API and only that page comes
     * back, so a school with thousands of requests never ships the lot to
     * the browser to slice locally. `total` is the count the API reports for
     * the whole filtered set, which is what TablePagination counts against.
     */
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [openRow, setOpenRow] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listProfileChangeRequestsService({
                page: page + 1, limit: rowsPerPage, search, status,
            });
            setRows(res?.data?.tableData || []);
            setTotal(Number(res?.data?.total) || 0);
        } catch (e) {
            // An empty queue and a failed fetch look identical otherwise.
            toast(TOAST_TYPES.ERROR, 'Could not load requests', e?.message || 'Please try again.');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, search, status]);

    useEffect(() => { load(); }, [load]);

    const decide = async (approve) => {
        if (!openRow?._id || busy) return;
        setBusy(true);
        try {
            await reviewProfileChangeRequestService({ request_id: openRow._id, approve });
            toast(
                TOAST_TYPES.SUCCESS,
                approve ? 'Approved' : 'Rejected',
                approve
                    ? `${openRow.learner_name || 'The learner'}’s profile has been updated.`
                    : `${openRow.learner_name || 'The learner'}’s profile was left unchanged.`,
            );
            setOpenRow(null);
            load();
        } catch (e) {
            toast(TOAST_TYPES.ERROR, 'Could not save', e?.message || 'Please try again.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3, maxWidth: 1240, mx: 'auto', width: '100%' }}>
            <Typography sx={{ fontSize: { xs: '24px', sm: '30px' }, fontWeight: 800, color: NAVY }}>
                Profile Change Requests
            </Typography>
            <Typography sx={{ fontSize: '14px', color: MUTED, mt: 0.5, mb: 3 }}>
                Students submit these when they confirm or correct their details on first login.
                Nothing changes on their profile until you approve it here.
            </Typography>

            <Box sx={{
                border: `1px solid ${BORDER}`, borderRadius: '16px',
                backgroundColor: 'var(--brand-surface, #fff)', overflow: 'hidden',
            }}>
                <Box sx={{ display: 'flex', gap: 2, p: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search by student or class…"
                        value={search}
                        onChange={(e) => { setPage(0); setSearch(e.target.value); }}
                        sx={{ flex: '1 1 260px', maxWidth: 380 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon style={{ fontSize: 18, color: MUTED }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        size="small" select value={status}
                        onChange={(e) => { setPage(0); setStatus(e.target.value); }}
                        sx={{ minWidth: 150 }}
                    >
                        <MenuItem value="">All statuses</MenuItem>
                        <MenuItem value={PROFILE_CHANGE_STATUS.PENDING}>Pending</MenuItem>
                        <MenuItem value={PROFILE_CHANGE_STATUS.APPROVED}>Approved</MenuItem>
                        <MenuItem value={PROFILE_CHANGE_STATUS.REJECTED}>Rejected</MenuItem>
                    </TextField>
                </Box>

                <Box sx={{
                    display: 'grid', gridTemplateColumns: COLS, gap: 2,
                    px: 2.5, py: 1.5, borderTop: `1px solid ${BORDER}`,
                    borderBottom: `1px solid ${BORDER}`,
                }}>
                    {['Student', 'Class', 'Submitted', 'Fields changed', 'Status', ''].map((h, i) => (
                        <Typography key={i} sx={{
                            fontSize: '11.5px', fontWeight: 700, color: MUTED,
                            textTransform: 'uppercase', letterSpacing: '0.5px',
                        }}>{h}</Typography>
                    ))}
                </Box>

                {loading ? (
                    <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
                ) : rows.length === 0 ? (
                    <Typography sx={{ py: 6, textAlign: 'center', color: MUTED, fontSize: '14px' }}>
                        No profile change requests yet.
                    </Typography>
                ) : rows.map((r) => {
                    const pending = Number(r.status) === PROFILE_CHANGE_STATUS.PENDING;
                    const n = (r.changes || []).length;
                    return (
                        <Box key={r._id} sx={{
                            display: 'grid', gridTemplateColumns: COLS, gap: 2,
                            px: 2.5, py: 2, alignItems: 'center',
                            borderBottom: `1px solid ${BORDER}`,
                            '&:last-of-type': { borderBottom: 'none' },
                        }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: NAVY }}>
                                {r.learner_name || '—'}
                            </Typography>
                            {/* Blank for a learner who belongs to no class. */}
                            <Typography sx={{ fontSize: '14px', color: MUTED }}>{r.class_name || '—'}</Typography>
                            <Typography sx={{ fontSize: '14px', color: MUTED }}>{fmtDate(r.createdAt)}</Typography>
                            <Typography sx={{ fontSize: '14px', color: MUTED }}>
                                {n} {n === 1 ? 'field' : 'fields'}
                            </Typography>
                            <Box><StatusChip status={r.status} /></Box>
                            <Box sx={{ textAlign: 'right' }}>
                                <Button variant="outlined" sx={outlineBtnSx} onClick={() => setOpenRow(r)}>
                                    {pending ? 'Review' : 'View'}
                                </Button>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {/*
              * The same control the client, peer and program lists use, rather
              * than a bespoke pair of buttons — a reviewer moving between them
              * should not have to learn a second way to page.
              */}
            <TablePagination
                component="div"
                count={total}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                onPageChange={(_e, next) => setPage(next)}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    // Back to the first page: page 4 of the old size may not
                    // exist at the new one, and the API would return nothing.
                    setPage(0);
                }}
            />

            <ReviewDialog
                open={!!openRow}
                request={openRow}
                busy={busy}
                onClose={() => setOpenRow(null)}
                onDecide={decide}
            />
        </Box>
    );
};

export default ProfileChangeRequestsPage;
